import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ColDef, CellClickedEvent, CheckboxSelectionCallback, RowClassRules, RowClassParams } from '@ag-grid-community/core';
import { TASK_SEARCH_MODE, TaskSearchMode } from '../../config/task.config';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { TranslateService } from '@ngx-translate/core';
import { getTasksColumnDefs, isPseSyncFailed } from '../../utils/task.utils';
import { GO_URL, MAP_URL, TASKS_URL } from '../../../../config/url.config';
import { Router } from '@angular/router';
import { GridMasterComponent } from '../../../shared/components/_grid/grid-master/grid-master.component';
import { StoreService } from '../../../../services/store.service';
import { catchError, combineLatest, concatMap, delay, filter, finalize, forkJoin, from, map, of, pairwise, startWith, Subject, Subscription, switchMap, take, tap, toArray } from 'rxjs';
import { ConnectionStatusService } from 'src/app/services/connection-status.service';
import { ACTION_COLUMN } from 'src/app/config/grid.config';
import { OfflineService } from 'src/app/services/offline.service';
import { TasksService } from '../../services/tasks.service';
import { SettingsKey } from '../../../../model/settings.model';
import { MatDialog } from '@angular/material/dialog';
import { InputDialogComponent } from '../../../shared/components/_dialog/input-dialog/input-dialog.component';
import { SyncProgressDialogComponent } from '../../components/_dialog/sync-progress-dialog/sync-progress-dialog.component';
import {
  isPseToCorrect,
  Task,
  TaskAssignResponse,
  TaskMultiAcceptResponse,
  TaskStatusChangeResponse
} from '../../model/task.model';
import { DialogService } from '../../../../services/dialog.service';
import { DataService } from 'src/app/modules/data/services/data.service';
import { FailureService } from 'src/app/services/failure.service';
import { LatitudeLongitude } from '../../model/form.model';
import { SessionService } from 'src/app/services/session.service';

@Component({
  selector: 'tasks-page',
  templateUrl: './tasks-page.component.html',
  styleUrls: []
})
export class TasksPageComponent implements OnInit, OnDestroy {
  @ViewChild('grid') grid: GridMasterComponent;
  TASK_SEARCH_MODE = TASK_SEARCH_MODE;

  checkboxSelection: boolean | CheckboxSelectionCallback = false;
  idsToFilter: number[] = [];

  private filterTrigger$ = new Subject<(tasks: Task[]) => Task[]>();

  tasks$ = combineLatest([
    this.tasksService.tasks$.pipe(tap(() => this.resetFilter())),
    this.offlineService.offlineDataChanged$,
    this.filterTrigger$.pipe(startWith((tasks: Task[]) => tasks)), // Domyślna funkcja filtrowania
  ]).pipe(
    switchMap(([tasks, _, filterFn]) =>
      this.tasksService.determineOfflineStatus(tasks).pipe(
        map((determinedTasks: Task[]) => filterFn(determinedTasks)) // Stosowanie funkcji filtrowania
      )
    ),
    tap(() => this.grid?.gridApi?.refreshCells()) // Odświeżanie komórek w siatce
  );

  columnDefs: ColDef[] = getTasksColumnDefs(this.translate);

  private sub = new Subscription();
  isOnline: boolean = true;

  rowClassRules: RowClassRules =
    {
      'red': (params: RowClassParams<Task>) => Boolean(params.data && params.data.copiedFrom && params.data.status === 'IN_PROGRESS'),
      'gray': (params) => params.data?.assignedTo === null,
      'orange': (params: RowClassParams<Task>) => isPseToCorrect(params.data),
      'yellow': (params: RowClassParams<Task>) => isPseSyncFailed(params.data),
      'text-gray': (params) =>
        params.data?.assignedTo !== null &&
        params.data?.assignedTo?.id !== this.store.loggedUser.id,
    };


  constructor(
    public tasksService: TasksService,
    private translate: TranslateService,
    private router: Router,
    public store: StoreService,
    public connectionStatusService: ConnectionStatusService,
    private offlineService: OfflineService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private dataService: DataService,
    private failureService: FailureService,
    private sessionService: SessionService
  ) {}

  modes$ = combineLatest([
    this.connectionStatusService.networkStatus$.pipe(
      tap((isOnline) => {
        this.isOnline = isOnline;
        this.grid?.gridApi?.redrawRows();
      })
    ),
    this.tasksService.getTasksListMode(),
  ]).pipe(
    delay(0), // Wymuszenie asynchronicznego wykonania poniższych operatorów
    tap(([isOnline, tasksListMode]) => {
      if (!isOnline && tasksListMode !== TASK_SEARCH_MODE.MY) {
        this.tasksService.setTasksListMode(TASK_SEARCH_MODE.MY);
      }
      this.handleModeChange(tasksListMode, isOnline);
    }),
    map(([isOnline, tasksListMode]) => ({ isOnline, tasksListMode }))
  );

  ngOnInit() {
    this.checkIfFilterIsSet();
    this.subscribeOnlineRefresh();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  changeMode(event: MatButtonToggleChange) {
    this.tasksService.setTasksListMode(event.value);
  }

  handleModeChange(mode: TaskSearchMode, isOnline: boolean) {
    this.grid?.clearSelectedRows();
    if (mode === 'MY') {
      this.checkboxSelection = isOnline ? this.isRowSelectable : false;
    } else if (mode === 'ACTIVE') {
      this.checkboxSelection = isOnline ? this.isRowSelectable : false;
    } else if (
      (mode === 'ARCHIVED' && (this.canAcceptTasks() || this.store.isAdmin())) ||
      (mode === 'TO_ACCEPT' && (this.canEditTasks() || this.canMultiAccept()))
    ) {
      this.checkboxSelection = true;
    } else {
      this.checkboxSelection = false;
    }
    this.columnDefs = getTasksColumnDefs(
      this.translate,
      this.checkboxSelection
    );
  }

  isRowSelectable = <T extends { data: Task }>(params: T) => {
    if (params.data.status === 'CANCELLED') {
      return false;
    }

    return (
      this.store.isAdmin() ||
      (!params.data?.localVersion &&
        (!params.data.assignedTo
          || params.data.assignedTo?.id === this.store.loggedUser.id
          || params.data.status === 'TO_ACCEPT' || params.data.status === 'CLOSED'
        ))
    );
  };

  rowClicked(event: CellClickedEvent) {
    if (event.colDef.headerName !== ACTION_COLUMN) {
      void this.router.navigate([GO_URL, TASKS_URL, event.data.id]);
    }
  }

  goToTasksMap() {
    void this.router.navigate([GO_URL, MAP_URL]);
  }

  sendToPse() {
    if (!this.canSendToPse()) {
      return;
    }

    const tasks: Task[] = this.grid.selectedRows;
    if (tasks.length === 0) {
      this.store.showWarningMsg('TASKS.PSE_SEND.NO_SELECTION');
      return;
    }

    if (tasks.some((task) => task.order?.source !== 'PSE')) {
      this.store.showWarningMsg('TASKS.PSE_SEND.NOT_PSE_SOURCE');
      return;
    }

    const total = tasks.length;
    const setProgress = (sent: number) =>
      this.store.loaderText = this.translate.instant('TASKS.PSE_SEND.PROGRESS', { sent, total });

    this.store.showLoader();
    setProgress(0);
    from(tasks).pipe(
      concatMap((task, index) => this.tasksService.pushToPse(task.id).pipe(
        map((response) => ({ task: response, failed: isPseSyncFailed(response) })),
        catchError(() => of({ task, failed: true })),
        tap(() => setProgress(index + 1))
      )),
      toArray(),
      finalize(() => {
        this.store.loaderText = '';
        this.store.hideLoader();
      })
    ).subscribe((results) => {
      const failedNames = results.filter((result) => result.failed).map((result) => result.task.name);
      if (failedNames.length === 0) {
        this.store.showSuccessMsg(total === 1 ? 'TASKS.PSE_SEND.SUCCESS_SINGLE' : 'TASKS.PSE_SEND.SUCCESS_MULTI');
      } else if (failedNames.length === 1) {
        this.store.showWarningMsg('TASKS.PSE_SEND.FAILED_SINGLE', { name: failedNames[0] });
      } else {
        this.store.showWarningMsg('TASKS.PSE_SEND.FAILED_MULTI', { names: failedNames.join(', ') });
      }
      this.grid.clearSelectedRows();
    });
  }

  canSendToPse() {
    return this.store.isAdmin() || this.store.hasTaskReleaseRole();
  }

  canViewToAccept() {
    return (
      this.store.isAdmin() ||
      this.store.hasTaskEditRole() ||
      this.store.hasTaskApproveRole()
    );
  }

  canMultiAccept() {
    return this.store.canMultiApprove();
  }

  canAcceptTasks() {
    return this.store.hasTaskApproveRole();
  }

  canEditTasks(): boolean {
    return this.store.hasTaskEditRole();
  }

  canViewCurrent() {
    return this.store.isAdmin() || this.store.hasTaskReceiveRole();
  }

  canReceive() {
    return this.store.hasTaskReceiveRole();
  }

  canSynchronizeAll() {
    return this.store.hasTaskReceiveRole();
  }

  syncAll() {
    this.store.projectChanged$
      .pipe(
        take(1),
        switchMap((project) =>
          from(this.sessionService.refreshSessionForSync()).pipe(
            filter((isRefreshed) => isRefreshed),
            tap(() => this.openSyncDialog()),
            switchMap(() => this.tasksService.runSyncAllWithProgress(project!.id))
          )
        )
      )
      .subscribe();
  }

  private openSyncDialog(): void {
    this.dialog.open(SyncProgressDialogComponent, {
      disableClose: true,
      closeOnNavigation: false,
      hasBackdrop: true,
      panelClass: 'sync-progress-fullscreen-dialog',
      maxWidth: '100vw',
      width: '100vw',
      height: '100vh'
    });
  }

  addFilter() {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      data: this.store.tasksGridFilters.map((item) => item.name),
    });
    dialogRef.afterClosed().subscribe((name) => {
      if (name) {
        this.grid.saveSetup(SettingsKey.TASKS_GRID, name);
      }
    });
  }

  deleteFilter() {
    this.store.handleTasksGridFilterDelete();
  }

  updateFilter() {
    this.grid.saveSetup(
      SettingsKey.TASKS_GRID,
      this.store.tasksGridSelectedFilter
    );
  }

  restoreFilter() {
    this.store.handleTasksGridFilterChange(
      this.store.tasksGridSelectedFilter,
      true
    );
  }

  private checkIfFilterIsSet(): void {
    if (this.store.tasksGridSelectedFilter) {
      setTimeout(
        () =>
          this.store.handleTasksGridFilterChange(
            this.store.tasksGridSelectedFilter
          ),
        100
      );
    }
  }

  private subscribeOnlineRefresh(): void {
    this.sub.add(
      combineLatest([
        this.connectionStatusService.networkStatus$,
        this.tasksService.getTasksListMode()
      ]).pipe(
        pairwise(),
        filter(([[prevIsOnline], [isOnline, mode]]) =>
          !prevIsOnline &&
          isOnline &&
          !this.sessionService.isSyncDialogOpen() &&
          (mode === TASK_SEARCH_MODE.MY || mode === TASK_SEARCH_MODE.ACTIVE)
        )
      ).subscribe(() => this.tasksService.refreshTasks())
    );
  }

  resetGrid(): void {
    this.store.handleTasksGridFilterChange('');
    this.grid.reset();
  }

  moveToAccept() {
    this.store.showLoader();
    this.tasksService
      .reopen(this.getTaskIds())
      .subscribe((response) => this.handleSuccessResponse(response));
  }

  moveToInProgress() {
    this.store.showLoader();
    this.tasksService
      .revoke(this.getTaskIds())
      .subscribe((response) => this.handleSuccessResponse(response));
  }

  private handleSuccessResponse(
    response: TaskStatusChangeResponse,
    successMsg = 'TASKS_MOVED_SUCCESS',
    failureMsg = 'TASKS_MOVED_FAILED',
    partialMsg = 'TASKS_MOVED_PARTIAL'
  ) {
    const prefix = 'TASKS.';
    if (response.failed.length === 0) {
      this.store.showSuccessMsg(prefix + successMsg);
    } else if (response.succeeded.length === 0) {
      this.store.showErrorMsg(prefix + failureMsg);
    } else {
      this.store.showWarningMsg(prefix + partialMsg);
    }

    this.grid.clearSelectedRows();
    this.tasksService.refreshTasks();
  }
  private handleAssignSuccessResponse(
    response: TaskAssignResponse,
    successMsg = 'TASKS_ASSIGN_SUCCESS',
    failureMsg = 'TASKS_ASSIGN_FAILED',
    partialMsg = 'TASKS_ASSIGN_PARTIAL'
  ) {
    const prefix = 'TASKS.';
    if (response.failed.length === 0) {
      this.store.showSuccessMsg(prefix + successMsg);
    } else if (response.succeeded.length === 0) {
      this.store.showErrorMsg(prefix + failureMsg);
    } else {
      this.store.showWarningMsg(prefix + partialMsg);
    }

    this.grid.clearSelectedRows();
    this.tasksService.refreshTasks();
  }
  private getTaskIds(): number[] {
    return this.grid.selectedRows.map((item) => item.id);
  }

  multiAccept() {
    const ids = this.getTaskIds();

    this.dialogService
      .showTasksMultiApprovePrompt(ids.length)
      .afterClosed()
      .pipe(
        filter((result) => result), // Przepuszczamy tylko, gdy użytkownik potwierdził
        tap(() => this.store.showLoader()), // Pokazujemy loader
        switchMap(() =>
          this.tasksService
            .multiApprove(ids)
            .pipe(tap((response) => this.handleMultiApproveResponse(response)))
        ), // Wywołujemy usługę
        finalize(() => this.store.hideLoader()) // Ukrywamy loader niezależnie od wyniku
      )
      .subscribe();
  }

  triggerFilter(filterFn: (tasks: Task[]) => Task[]) {
    this.filterTrigger$.next(filterFn);
  }

  resetFilter() {
    this.idsToFilter = [];
    this.filterTrigger$.next((tasks: Task[]) => tasks);
  }

  private handleMultiApproveResponse(response: TaskMultiAcceptResponse) {
    const idsToRemove = response.succeeded.map((x) => x.id);
    this.idsToFilter = [...this.idsToFilter, ...idsToRemove];
    if (response.failed.length === 0) {
      this.store.showSuccessMsg('TASKS.MULTI_APPROVE_SUCCESS');
    } else {
      this.store.showErrorMsg('TASKS.MULTI_APPROVE_PARTIAL', {
        tasks: this.getTasksName(response),
      });
    }

    this.triggerFilter((tasks) =>
      tasks.filter((task) => !this.idsToFilter.includes(task.id))
    );
  }

  private getTasksName(response: TaskMultiAcceptResponse): string {
    return response.failed.map((item) => item.name).join(', ');
  }

  updateOfflineCache() {
    const date = new Date();
    const fromDate = new Date(
      date.getFullYear() - 6,
      date.getMonth(),
      date.getDate()
    )
      .toISOString()
      .split('T')[0];
    this.tasks$
      .pipe(
        take(1),
        tap(() => this.connectionStatusService.setSyncStatus(true)),
        map((tasks) => this.prepareFailureRequestsData(tasks)),
        switchMap((locations) => {
          return forkJoin([
            this.dataService.cacheLinePdfs(this.store.selectedProject.id),
            this.failureService.cacheHistoricalTowerFailures(
              locations.towerLocations,
              fromDate
            ),
            this.failureService.cacheHistoricalSpanFailures(
              [...locations.spanLocations.reduce((acc, item) => acc.set(`${item.latitude}|${item.longitude}`, item), new Map()).values()],
              fromDate
            ),
            //TODO: inne zadania wykonywane w celu aktualizacji cache
          ]);
        }),
        finalize(() => {
          this.store.showSuccessMsg('OFFLINE_DATA_SYNC_FINISHED');
          this.connectionStatusService.setSyncStatus(false);
        })
      )
      .subscribe();
  }

  private prepareFailureRequestsData(tasks: Task[]): {
    spanLocations: LatitudeLongitude[];
    towerLocations: LatitudeLongitude[];
  } {
    const locations: {
      spanLocations: LatitudeLongitude[];
      towerLocations: LatitudeLongitude[];
    } = {
      spanLocations: [],
      towerLocations: [],
    };
    return tasks.reduce((acc, task) => {
      if (task.span) {
        acc.spanLocations.push({
          latitude: task.span.latitude ?? ((task.span.startTower.latitude + task.span.endTower.latitude) / 2),
          longitude: task.span.longitude ?? ((task.span.startTower.longitude + task.span.endTower.longitude) / 2),
        });
      } else if (task.tower) {
        acc.towerLocations.push({
          latitude: task.tower.latitude,
          longitude: task.tower.longitude
        });
      }
      return acc;
    }, locations);
  }

  assignToMe() {
    this.store.showLoader();
    this.tasksService
      .assignTasks(this.getTaskIds(), this.store.loggedUser.id)
      .pipe(
        tap((response) => this.handleAssignSuccessResponse(response)),
        finalize(() => this.store.hideLoader())
      )
      .subscribe();
  }

  unassignFromMe() {
    this.store.showLoader();
    this.tasksService
      .unassignTasks(this.getTaskIds())
      .pipe(
        tap((response) => this.handleAssignSuccessResponse(response)),
        finalize(() => this.store.hideLoader())
      )
      .subscribe();
  }
}
